import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, HostListener } from '@angular/core';
import { FindJourneyDto } from '@interfaces/rest/interfaces/journey';
import { DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import * as moment from 'moment';

@Component({
  selector: 'app-created-service',
  templateUrl: './created-service.component.html',
  styleUrls: ['./created-service.component.scss'],
})
export class CreatedServiceComponent implements OnInit, OnDestroy {
  @Input() serviceData?: FindJourneyDto;
  @Output() duplicateMultiple = new EventEmitter<number>(); // Emite el número de copias a crear
  @Output() close = new EventEmitter<void>();

  timeRemaining = 10;
  private autoCloseTimer?: any;
  private countdownTimer?: any;

  // Estado para mostrar todo el contenido (direcciones + info + detalles adicionales)
  showFullDetails = false;

  // Estado para mostrar el panel de números
  showQuickDuplicatePanel = false;

  // Números para duplicación rápida (1-20)
  quickDuplicateNumbers: number[] = Array.from({ length: 20 }, (_, i) => i + 1);

  constructor(private utils: DpUtilsService) { }

  ngOnInit() {
    this.startAutoCloseTimer();
  }

  ngOnDestroy() {
    this.clearTimers();
  }

  // Escuchar teclas del teclado
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // ⚠️ IMPORTANTE: Ignorar atajos de teclado cuando el usuario está escribiendo
    // Esto previene que se disparen acciones mientras se escribe en inputs/textareas
    if (this.isUserTyping(event)) {
      return;
    }

    // Tecla D para mostrar/ocultar el panel de duplicación
    if (event.key === 'd' || event.key === 'D') {
      event.preventDefault();
      this.toggleQuickDuplicatePanel();
    }
    // Tecla Escape para cerrar
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
    }
  }

  /**
   * Verifica si el usuario está escribiendo en un campo de entrada
   * @param event Evento del teclado
   * @returns true si el usuario está en un input, textarea o elemento editable
   */
  private isUserTyping(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();

    // Verificar si es un campo de entrada estándar
    if (tagName === 'input' || tagName === 'textarea') {
      return true;
    }

    // Verificar si es un elemento contenteditable
    if (target.isContentEditable) {
      return true;
    }

    // Verificar si está dentro de un elemento con role="textbox"
    if (target.getAttribute('role') === 'textbox') {
      return true;
    }

    // Verificar si está dentro de un contenedor de Google Autocomplete
    // Los inputs de Google Places están dentro de gmp-place-autocomplete con Shadow DOM
    let currentElement: HTMLElement | null = target;
    while (currentElement) {
      // Verificar si el elemento está dentro de un contenedor con clase google-autocomplete-container
      if (currentElement.classList?.contains('google-autocomplete-container')) {
        return true;
      }

      // Verificar si es un elemento gmp-place-autocomplete (Web Component de Google Places)
      const currentTagName = currentElement.tagName?.toLowerCase();
      if (currentTagName === 'gmp-place-autocomplete') {
        return true;
      }

      // Verificar si está dentro del Shadow DOM de un gmp-place-autocomplete
      // Si el elemento está dentro de un Shadow Root, verificar el host
      if (currentElement.getRootNode && currentElement.getRootNode() instanceof ShadowRoot) {
        const shadowRoot = currentElement.getRootNode() as ShadowRoot;
        const host = shadowRoot.host;
        if (host.tagName?.toLowerCase() === 'gmp-place-autocomplete') {
          return true;
        }
        // Si es un input dentro de cualquier Shadow DOM, también es un campo de entrada
        if (currentTagName === 'input') {
          return true;
        }
      }

      // Moverse al elemento padre
      currentElement = currentElement.parentElement;
    }

    return false;
  }

  private startAutoCloseTimer() {
    // Countdown timer (actualizar cada segundo)
    this.countdownTimer = setInterval(() => {
      this.timeRemaining--;
      if (this.timeRemaining <= 0) {
        this.closeModal();
      }
    }, 1000);
  }

  private clearTimers() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
    }
  }

  closeModal() {
    this.clearTimers();
    this.close.emit();
  }

  toggleQuickDuplicatePanel() {
    this.showQuickDuplicatePanel = !this.showQuickDuplicatePanel;
    this.toggleDetails();
  }

  toggleDetails() {
    // Mostrar todo el contenido y detener el contador
    if (!this.showFullDetails) {
      this.showFullDetails = true;
    }
    this.clearTimers(); // Detener el contador automático
  }

  async onQuickDuplicate(count: number) {
    // Si el número es mayor a 5, mostrar confirmación
    if (count > 5) {
      const confirmed = await this.showConfirmation(count);
      if (!confirmed) return;
    }

    this.duplicateMultiple.emit(count);
  }

  private async showConfirmation(count: number): Promise<boolean> {
    const result = await this.utils.showAlert({
      header: 'Confirmar duplicación',
      message: `¿Estás seguro de crear ${count} servicios?`,
      cancel: true,
      wait: true,
      buttonOk: 'Crear',
      buttonCancel: 'Cancelar',
      cssClass: 'relocate',
    });

    return !!result;
  }

  getExtrasText(): string {
    if (!this.serviceData?.extra || this.serviceData.extra.length === 0) {
      return 'Ninguno';
    }

    const extrasMap: { [key: string]: string } = {
      'adapted_taxi': 'Taxi adaptado',
      'baby_seat': 'Silla de bebé',
      'animal': 'Animal',
      'bikes': 'Bicicletas',
      'scooters': 'Patinetes',
      'low_car': 'Coche bajo',
      '6_places': '6 plazas',
      'car5_2': 'Coche 5+2',
      'car4_2': 'Coche 4+2',
      'large_trunk': 'Maletero grande',
      'booster_seat': 'Elevador'
    };

    return this.serviceData.extra.map(e => extrasMap[e] || e).join(', ');
  }

  getOriginText(): string {
    if (!this.serviceData?.fromAddress) return '';
    return this.serviceData.fromAddress.title || '';
  }

  getOriginDescription(): string {
    if (!this.serviceData?.fromAddress) return '';
    return this.serviceData.fromAddress.description || '';
  }

  getDestinationText(): string {
    if (!this.serviceData?.toAddress) return '';
    return this.serviceData.toAddress.title || '';
  }

  getDestinationDescription(): string {
    if (!this.serviceData?.toAddress) return '';
    return this.serviceData.toAddress.description || '';
  }

  getReserveDateTime(): string {
    if (!this.serviceData?.reserveDate) return '';

    // Formatear la fecha de reserva
    const reserveDate = moment(this.serviceData.reserveDate);
    return reserveDate.format('DD/MM/YYYY HH:mm');
  }

  getClientName(): number {
    // toAnotherUser es un ID de usuario (string)
    // Por ahora mostramos el ID si existe, o vacío si no
    // TODO: Idealmente tendríamos el nombre del usuario completo
    return this.serviceData?.toAnotherUser || 0;
  }

  // Porcentaje de progreso para la barra
  get progressPercentage(): number {
    return ((10 - this.timeRemaining) / 10) * 100;
  }

  getFirstLineNumbers(): number[] {
    return this.quickDuplicateNumbers.slice(0, 10);
  }

  getSecondLineNumbers(): number[] {
    return this.quickDuplicateNumbers.slice(10, 20);
  }
}
